import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { useAppContext } from '../context/AppContext';
import { icons } from '../components/ui/Icons';
import Button from '../components/ui/Button';
import ImageUpload from '../components/ImageUpload';
import InvitationModal from '../components/InvitationModal';
import {
  updateBarbershopBasicInfo,
  updateBarbershopContactInfo,
  updateBarbershopServices,
} from '../services/barbershopService';
import {
  getBarbershopProfessionals,
  getBarbershopInvitations,
  cancelInvitation,
  removeProfessional
} from '../services/invitationService';

// Schema de validação para informações básicas
const basicInfoSchema = z.object({
  name: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
  description: z.string().min(10, 'Descrição deve ter pelo menos 10 caracteres'),
  city: z.string().min(2, 'Cidade é obrigatória'),
  bannerUrl: z.string().url('URL inválida').optional().or(z.literal('')),
});

// Schema para contato
const contactInfoSchema = z.object({
  address: z.string().min(5, 'Endereço completo é obrigatório'),
  phone: z.string().min(10, 'Telefone da barbearia é obrigatório').regex(/^[\d\s()+-]+$/, 'Telefone inválido'),
});

/**
 * Página: BarbershopEditPage
 * Permite donos editarem todas as informações da barbearia
 */
const BarbershopEditPage = () => {
  const navigate = useNavigate();
  const { currentUser, barbershops } = useAppContext();

  const [activeTab, setActiveTab] = useState('basic'); // basic, contact, services, barbers
  const [services, setServices] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bannerUrl, setBannerUrl] = useState('');
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [loadingProfessionals, setLoadingProfessionals] = useState(false);

  // Estados para adicionar novo serviço
  const [newService, setNewService] = useState({ name: '', price: '', durationValue: '', durationUnit: 'min' });

  // Buscar barbearia do dono (suporta formato antigo e novo)
  const getBarbershopId = () => {
    if (currentUser?.barbershopId) {
      // Formato antigo: string barbershopId
      return currentUser.barbershopId;
    }
    if (currentUser?.barbershops && currentUser.barbershops.length > 0) {
      // Formato novo: array barbershops - pegar a primeira
      return currentUser.barbershops[0];
    }
    return null;
  };

  const barbershopId = getBarbershopId();
  const barbershop = barbershops.find(b => b.id === barbershopId);

  // Form para informações básicas
  const {
    register: registerBasic,
    handleSubmit: handleSubmitBasic,
    formState: { errors: errorsBasic },
    reset: resetBasic,
  } = useForm({
    resolver: zodResolver(basicInfoSchema),
    defaultValues: {
      name: '',
      description: '',
      city: '',
      bannerUrl: '',
    },
  });

  // Form para contato
  const {
    register: registerContact,
    handleSubmit: handleSubmitContact,
    formState: { errors: errorsContact },
    reset: resetContact,
  } = useForm({
    resolver: zodResolver(contactInfoSchema),
    defaultValues: {
      address: '',
      phone: '',
    },
  });

  // Carregar dados da barbearia ao montar
  useEffect(() => {
    if (barbershop) {
      resetBasic({
        name: barbershop.name || '',
        description: barbershop.description || '',
        city: barbershop.city || '',
        bannerUrl: barbershop.bannerUrl || '',
      });
      resetContact({
        address: barbershop.address || '',
        phone: barbershop.phone || '',
      });
      // Garantir que services é sempre um array
      setServices(Array.isArray(barbershop.services) ? barbershop.services : []);
      setBannerUrl(barbershop.bannerUrl || '');
      
      // Carregar profissionais e convites
      loadProfessionalsAndInvitations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barbershop, resetBasic, resetContact]);

  // Função para carregar profissionais e convites pendentes
  const loadProfessionalsAndInvitations = async () => {
    if (!barbershop) return;
    
    setLoadingProfessionals(true);
    try {
      const [professionalsResult, invitationsResult] = await Promise.all([
        getBarbershopProfessionals(barbershop.id),
        getBarbershopInvitations(barbershop.id)
      ]);
      
      setProfessionals(professionalsResult);
      setPendingInvitations(invitationsResult.filter(inv => inv.status === 'pending'));
    } catch (error) {
      console.error('Erro ao carregar profissionais e convites:', error);
      toast.error('Erro ao carregar profissionais');
    } finally {
      setLoadingProfessionals(false);
    }
  };

  // Verificar se usuário é dono
  if (!currentUser || currentUser.role !== 'owner') {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-xl mb-4">Você não tem permissão para acessar esta página.</p>
          <Button onClick={() => navigate('/')}>Voltar para Home</Button>
        </div>
      </div>
    );
  }

  if (!barbershop) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-xl mb-4">Barbearia não encontrada.</p>
          <Button onClick={() => navigate('/')}>Voltar para Home</Button>
        </div>
      </div>
    );
  }

  // Salvar informações básicas
  const onSubmitBasicInfo = async (data) => {
    setIsSubmitting(true);
    const updateData = {
      ...data,
      bannerUrl: bannerUrl || data.bannerUrl,
    };
    const result = await updateBarbershopBasicInfo(barbershop.id, updateData);
    setIsSubmitting(false);

    if (result.success) {
      toast.success('Informações básicas atualizadas!');
    } else {
      toast.error(result.error || 'Erro ao atualizar informações');
    }
  };

  const handleBannerUploaded = (url) => {
    setBannerUrl(url);
  };

  // Salvar informações de contato
  const onSubmitContactInfo = async (data) => {
    setIsSubmitting(true);
    const result = await updateBarbershopContactInfo(barbershop.id, data);
    setIsSubmitting(false);

    if (result.success) {
      toast.success('Informações de contato atualizadas!');
    } else {
      toast.error(result.error || 'Erro ao atualizar contato');
    }
  };

  // Adicionar novo serviço
  const handleAddService = () => {
    if (!newService.name || !newService.price) {
      toast.error('Nome e preço são obrigatórios');
      return;
    }

    const price = parseFloat(newService.price);
    if (isNaN(price) || price <= 0) {
      toast.error('Preço inválido');
      return;
    }

    // Montar string de duração
    let duration = '';
    if (newService.durationValue) {
      duration = `${newService.durationValue} ${newService.durationUnit}`;
    }

    setServices([...services, { 
      name: newService.name, 
      price: price, 
      duration: duration || 'A combinar'
    }]);
    setNewService({ name: '', price: '', durationValue: '', durationUnit: 'min' });
  };

  // Remover serviço
  const handleRemoveService = (index) => {
    setServices(services.filter((_, i) => i !== index));
  };

  // Salvar serviços
  const handleSaveServices = async () => {
    setIsSubmitting(true);
    const result = await updateBarbershopServices(barbershop.id, services);
    setIsSubmitting(false);

    if (result.success) {
      toast.success('Serviços atualizados!');
    } else {
      toast.error(result.error || 'Erro ao atualizar serviços');
    }
  };

  // Cancelar convite pendente
  const handleCancelInvitation = async (invitationId) => {
    try {
      await cancelInvitation(invitationId, currentUser.uid);
      toast.success('Convite cancelado');
      loadProfessionalsAndInvitations();
    } catch (error) {
      console.error('Erro ao cancelar convite:', error);
      toast.error(error.message || 'Erro ao cancelar convite');
    }
  };

  // Remover profissional
  const handleRemoveProfessional = async (professionalId) => {
    if (!window.confirm('Tem certeza que deseja remover este profissional da barbearia?')) {
      return;
    }

    try {
      await removeProfessional(barbershop.id, professionalId, currentUser.uid);
      toast.success('Profissional removido');
      loadProfessionalsAndInvitations();
    } catch (error) {
      console.error('Erro ao remover profissional:', error);
      toast.error(error.message || 'Erro ao remover profissional');
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Cabeçalho */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/barbershop/${barbershop.id}`)}
            className="flex items-center text-indigo-600 hover:text-indigo-800 mb-4"
          >
            {icons.chevronLeft({ className: 'h-5 w-5 mr-1' })}
            Voltar para barbearia
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Editar Barbearia</h1>
          <p className="text-gray-600 mt-2">Gerencie as informações da sua barbearia</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="flex border-b overflow-x-auto">
            <button
              onClick={() => setActiveTab('basic')}
              className={`px-6 py-4 font-semibold whitespace-nowrap ${
                activeTab === 'basic'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Informações Básicas
            </button>
            <button
              onClick={() => setActiveTab('contact')}
              className={`px-6 py-4 font-semibold whitespace-nowrap ${
                activeTab === 'contact'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Contato
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`px-6 py-4 font-semibold whitespace-nowrap ${
                activeTab === 'services'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Serviços e Preços
            </button>
            <button
              onClick={() => setActiveTab('barbers')}
              className={`px-6 py-4 font-semibold whitespace-nowrap ${
                activeTab === 'barbers'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Profissionais
            </button>
          </div>

          <div className="p-6">
            {/* Tab: Informações Básicas */}
            {activeTab === 'basic' && (
              <form onSubmit={handleSubmitBasic(onSubmitBasicInfo)} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nome da Barbearia *
                  </label>
                  <input
                    type="text"
                    {...registerBasic('name')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: Barbearia Premium"
                  />
                  {errorsBasic.name && (
                    <p className="text-red-500 text-sm mt-1">{errorsBasic.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Descrição *
                  </label>
                  <textarea
                    {...registerBasic('description')}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Descreva sua barbearia..."
                  />
                  {errorsBasic.description && (
                    <p className="text-red-500 text-sm mt-1">{errorsBasic.description.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Cidade *
                  </label>
                  <input
                    type="text"
                    {...registerBasic('city')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: São Paulo"
                  />
                  {errorsBasic.city && (
                    <p className="text-red-500 text-sm mt-1">{errorsBasic.city.message}</p>
                  )}
                </div>

                <div>
                  <ImageUpload
                    currentImageUrl={bannerUrl}
                    onImageUploaded={handleBannerUploaded}
                    label="Banner da Barbearia"
                    aspectRatio="aspect-video"
                    maxSizeMB={5}
                    recommendedSize="1200x400px"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    O banner será exibido no topo da página da sua barbearia
                  </p>
                </div>

                <Button type="submit" disabled={isSubmitting} className="w-full">
                  {isSubmitting ? 'Salvando...' : 'Salvar Informações Básicas'}
                </Button>
              </form>
            )}

            {/* Tab: Contato */}
            {activeTab === 'contact' && (
              <form onSubmit={handleSubmitContact(onSubmitContactInfo)} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Endereço Completo da Barbearia *
                  </label>
                  <input
                    type="text"
                    {...registerContact('address')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Rua, número, complemento, bairro - CEP"
                  />
                  {errorsContact.address && (
                    <p className="text-red-500 text-sm mt-1">{errorsContact.address.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Este endereço será exibido para os clientes</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Telefone da Barbearia *
                  </label>
                  <input
                    type="tel"
                    {...registerContact('phone')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="(11) 98765-4321"
                  />
                  {errorsContact.phone && (
                    <p className="text-red-500 text-sm mt-1">{errorsContact.phone.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Telefone de contato da barbearia (pode ser diferente do seu pessoal)</p>
                </div>

                <Button type="submit" disabled={isSubmitting} className="w-full">
                  {isSubmitting ? 'Salvando...' : 'Salvar Informações de Contato'}
                </Button>
              </form>
            )}

            {/* Tab: Serviços */}
            {activeTab === 'services' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Serviços Cadastrados</h3>
                  
                  {!Array.isArray(services) || services.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Nenhum serviço cadastrado ainda.</p>
                  ) : (
                    <div className="space-y-3 mb-6">
                      {services.map((service, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{service.name}</h4>
                            <p className="text-sm text-gray-600">
                              R$ {typeof service.price === 'number' ? service.price.toFixed(2) : service.price} • {service.duration}
                            </p>
                          </div>
                          <button
                            onClick={() => handleRemoveService(index)}
                            className="text-red-500 hover:text-red-700 p-2"
                          >
                            {icons.xCircle({ className: 'h-5 w-5' })}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Adicionar novo serviço */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Adicionar Novo Serviço</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Nome *</label>
                      <input
                        type="text"
                        value={newService.name}
                        onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="Corte Masculino"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Preço (R$) *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={newService.price}
                        onChange={(e) => setNewService({ ...newService, price: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="45.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Duração (opcional)</label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="1"
                          value={newService.durationValue}
                          onChange={(e) => setNewService({ ...newService, durationValue: e.target.value })}
                          className="w-2/3 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                          placeholder="30"
                        />
                        <select
                          value={newService.durationUnit}
                          onChange={(e) => setNewService({ ...newService, durationUnit: e.target.value })}
                          className="w-1/3 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="min">min</option>
                          <option value="h">horas</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <Button onClick={handleAddService} variant="secondary" className="w-full mb-4">
                    {icons.plus({ className: 'h-5 w-5 inline mr-2' })}
                    Adicionar Serviço
                  </Button>
                </div>

                <Button onClick={handleSaveServices} disabled={isSubmitting} className="w-full">
                  {isSubmitting ? 'Salvando...' : 'Salvar Lista de Serviços'}
                </Button>
              </div>
            )}

            {/* Tab: Barbeiros */}
            {activeTab === 'barbers' && (
              <div className="space-y-6">
                {/* Profissionais Ativos */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">Profissionais Ativos</h3>
                    <Button 
                      onClick={() => setShowInvitationModal(true)} 
                      variant="secondary"
                      className="flex items-center"
                    >
                      {icons.plus({ className: 'h-5 w-5 mr-2' })}
                      Convidar Profissional
                    </Button>
                  </div>
                  
                  {loadingProfessionals ? (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                  ) : !Array.isArray(professionals) || professionals.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <p className="mt-2 text-gray-500">Nenhum profissional ativo ainda.</p>
                      <p className="text-sm text-gray-400">Clique em "Convidar Profissional" para adicionar.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {professionals.map((professional) => (
                        <div key={professional.id} className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                          <div className="flex items-center flex-1">
                            {professional.photoURL ? (
                              <img
                                src={professional.photoURL}
                                alt={professional.name}
                                className="w-12 h-12 rounded-full object-cover mr-4"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-lg mr-4">
                                {professional.name ? professional.name.split(' ').map(n => n[0]).join('').toUpperCase() : '?'}
                              </div>
                            )}
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{professional.name || 'Sem nome'}</h4>
                              <p className="text-sm text-gray-600">
                                {professional.specialty || 'Não especificada'} • {professional.experience || 'Não informada'}
                              </p>
                              <p className="text-xs text-gray-400">{professional.email}</p>
                            </div>
                          </div>
                          {professional.id !== currentUser.uid && (
                            <button
                              onClick={() => handleRemoveProfessional(professional.id)}
                              className="text-red-500 hover:text-red-700 p-2 ml-2"
                              title="Remover profissional"
                            >
                              {icons.xCircle({ className: 'h-5 w-5' })}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Convites Pendentes */}
                {pendingInvitations.length > 0 && (
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Convites Pendentes</h3>
                    <div className="space-y-3">
                      {pendingInvitations.map((invitation) => (
                        <div key={invitation.id} className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">
                              Convite para: {invitation.invitedUserName || 'Usuário'}
                            </p>
                            <p className="text-sm text-gray-600">
                              {invitation.invitedUserEmail}
                            </p>
                            <p className="text-sm text-gray-600">
                              Enviado em: {invitation.createdAt?.toDate().toLocaleDateString('pt-BR')}
                            </p>
                            <p className="text-xs text-gray-500">
                              Expira em: {invitation.expiresAt?.toDate().toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                          <button
                            onClick={() => handleCancelInvitation(invitation.id)}
                            className="text-red-500 hover:text-red-700 px-3 py-1 text-sm border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                          >
                            Cancelar
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg className="h-5 w-5 text-blue-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm text-blue-800">
                      <p className="font-semibold mb-1">Como funciona o sistema de convites:</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-700">
                        <li>Busque usuários cadastrados no Barberize</li>
                        <li>Envie um convite para se tornarem profissionais da sua barbearia</li>
                        <li>O usuário receberá uma notificação e poderá aceitar ou recusar</li>
                        <li>Ao aceitar, ele se tornará co-proprietário e poderá gerenciar a barbearia</li>
                        <li>Os profissionais podem adicionar especialidade e experiência no seu perfil</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Modal de Convite */}
      <InvitationModal
        isOpen={showInvitationModal}
        onClose={() => {
          setShowInvitationModal(false);
          loadProfessionalsAndInvitations();
        }}
        barbershop={barbershop}
        currentUserId={currentUser.id}
        currentUserName={currentUser.name}
      />
    </div>
  );
};

export default BarbershopEditPage;
